import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import backgroundImg from "../assets/visitenkarteDieWerkstatt.jpeg";
import data from "./data/workshops.json";

// Bild-Imports (werden per imageKey zugeordnet)
import workshop_muttertagsherz from "../assets/workshops/bilder_resized/workshop_muttertagsherz.webp";
import workshop_oesterliche_tischdeko from "../assets/workshops/bilder_resized/workshop_oesterliche_tischdeko.webp";
import workshop_oesterlicher_tuer_tischkranz_kartoffelkult from "../assets/workshops/bilder_resized/workshop_oesterlicher_tuer_tischkranz_kartoffelkult.webp";
import workshop_oesterlicher_tuer_tischkranz from "../assets/workshops/bilder_resized/workshop_oesterlicher_tuer_tischkranz.webp";
import workshop_oesterlichesNest from "../assets/workshops/bilder_resized/workshop_oesterlichesNest.webp";
import workshop_oliven_rosmarinkranz from "../assets/workshops/bilder_resized/workshop_oliven_rosmarinkranz.webp";
import workshop_sommerkraenze_eintrocknene from "../assets/workshops/bilder_resized/workshop_sommerkraenze_eintrocknene.webp";
import workshop_sommersträuße from "../assets/workshops/bilder_resized/workshop_sommersträuße.webp";
import workshop_hortensien_eintrocknen_herbst from "../assets/workshops/bilder_resized/workshop_hortensien_eintrocknen_herbst.webp";
import workshop_tischgirlande_herbst from "../assets/workshops/bilder_resized/workshop_tischgirlande_herbst.webp";
import workshop_herbstliche_tischdekoration from "../assets/workshops/bilder_resized/workshop_herbstliche_tischdekoration.webp";
import workshop_adv_tuerkranz from "../assets/workshops/bilder_resized/workshop_adv_tuerkranz.webp";
import workshop_adv_Tuerdeko_tannenbaum from "../assets/workshops/bilder_resized/workshop_adv_Tuerdeko_tannenbaum.webp";
import workshop_adv_tuerkranz_2 from "../assets/workshops/bilder_resized/workshop_adv_tuerkranz_2.webp";
import workshop_laengliches_Adventsgesteck from "../assets/workshops/bilder_resized/workshop_laengliches_Adventsgesteck.webp";
import workshop_Adventskranz from "../assets/workshops/bilder_resized/workshop_Adventskranz.webp";

// Map von imageKey -> importiertem Bild
const imageMap = {
  workshop_muttertagsherz,
  workshop_oesterliche_tischdeko,
  workshop_oesterlicher_tuer_tischkranz_kartoffelkult,
  workshop_oesterlicher_tuer_tischkranz,
  workshop_oesterlichesNest,
  workshop_oliven_rosmarinkranz,
  workshop_sommerkraenze_eintrocknene,
  workshop_sommersträuße,
  workshop_tischgirlande_herbst,
  workshop_hortensien_eintrocknen_herbst,
  workshop_herbstliche_tischdekoration,
  workshop_adv_tuerkranz,
  workshop_adv_Tuerdeko_tannenbaum,
  workshop_adv_tuerkranz_2,
  workshop_laengliches_Adventsgesteck,
  workshop_Adventskranz
};

function Hero() {
  return (
    <header
      className="hero hero--workshops"
      style={{ backgroundImage: `url(${backgroundImg})` }}
    >
      <div className="container hero__inner">
        <h1 className="text-align-center">DIY Workshop-Angebote</h1>
        <p className="lead">
          Möchtest DU auch dabei sein? Dann schnell anmelden zu den DIY Floristik Workshops
        </p>
        <p className="lead">Dauer ca. 2-2,5 Std</p>
        <p className="lead">Die Teilnehmerzahl ist pro Kurs auf 8 Personen begrenzt.</p>
        <p className="lead">
          Anmeldung direkt unter: 0175/1952530 oder über das Kontaktformular.
        </p>
        <p className="lead">Ich freue mich auf Dich!</p>
      </div>
    </header>
  );
}

function Workshops() {
  const itemsRef = useRef([]);

  // Einblenden per IntersectionObserver, mit gestaffelter Verzögerung
  useEffect(() => {
    const els = itemsRef.current.filter(Boolean);
    if (!("IntersectionObserver" in window) || els.length === 0) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-visible");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 }
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <main className="section section--page">
      <div className="container">
        <ul className="workshop-list">
          {data.map((w, idx) => (
            <li
              key={w.id}
              className="workshop-item"
              ref={(el) => (itemsRef.current[idx] = el)}
              style={{ animationDelay: `${idx * 90}ms` }}
            >
              {/* Bild */}
              <div className="workshop-col workshop-col--image">
                <div
                  className="workshop-image"
                  style={{ backgroundImage: `url(${imageMap[w.imageKey]})` }}
                  aria-label={`${w.ort}, ${w.adresse}`}
                />
              </div>

              {/* Ort / Adresse */}
              <div className="workshop-col workshop-col--place">
                <div className="workshop-label">Ort</div>
                <div className="workshop-place">
                  <strong>{w.ort}</strong>
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                      `${w.adresse_StrNR}, ${w.adresse_plzOrt}`
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="workshop-address-link"
                    aria-label={`Adresse in Google Maps öffnen: ${w.adresse_StrNR}, ${w.adresse_plzOrt}`}
                  >
                    <div className="workshop-address">{w.adresse_StrNR}</div>
                    <div className="workshop-address">{w.adresse_plzOrt}</div>
                  </a>
                  <div className="workshop-address2">{w.datum}</div>
                  <div className="workshop-address2">{w.uhr}</div>
                </div>
              </div>


              {/* Beschreibung */}
              <div className="workshop-col workshop-col--desc">
                <div className="workshop-label">Beschreibung</div>
                <p className="workshop-text">{w.beschreibung}</p>
              </div>

              {/* Preis */}
              <div className="workshop-col workshop-col--price">
                <div className="workshop-label">Preis</div>
                <p className="workshop-price">{w.preis}</p>
                <Link
                  to={`/kontakt?type=workshop&workshopId=${w.id}`}
                  className="btn"
                >
                  Anfragen
                </Link>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </main>
  );
}

export default function App() {
  return (
    <>
      <Hero />
      <Workshops />
    </>
  );
}

